import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  UserPlus, CheckCircle, AlertTriangle, AlertCircle, Clock,
  MapPin, Calendar as CalendarIcon, Users, Zap, ChevronRight,
} from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Footer from '../components/common/Footer';
import CountrySelect from '../components/common/CountrySelect';
import { eventAPI, attendeeAPI, registrationLinkAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import { getActiveUniqueFields, findFieldByName } from '../utils/fieldHelpers';
import { validateAttendeeDataFormat } from '../utils/validateDataFormat';
import defaultLogo from '../assets/logo_white_bg.png';

interface RecentEntry {
  id: string;
  name: string;
  time: Date;
  fields: Record<string, string>;
}

const UsherRegistration: React.FC = () => {
  const toast = useToastContext();
  const [searchParams] = useSearchParams();
  const [selectedEvent, setSelectedEvent] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [recentEntries, setRecentEntries] = useState<RecentEntry[]>([]);
  const [flashSuccess, setFlashSuccess] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Fetch registration config (same as SelfRegistration)
  const { data: registrationData, isLoading: fieldsLoading, error: fieldsError } = useQuery({
    queryKey: ['registration-config', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { data: {} };
      const tokenFromUrl = searchParams.get('token');
      if (tokenFromUrl) {
        const response = await registrationLinkAPI.getConfigByToken(tokenFromUrl);
        return response;
      } else {
        const response = await eventAPI.getRegistrationConfig(selectedEvent);
        return response.data;
      }
    },
    enabled: !!selectedEvent,
  });

  // Auto-select event from URL
  useEffect(() => {
    const eventFromUrl = searchParams.get('event');
    const tokenFromUrl = searchParams.get('token');
    if (eventFromUrl && !selectedEvent) setSelectedEvent(eventFromUrl);
    else if (tokenFromUrl && !selectedEvent) setSelectedEvent(tokenFromUrl);
  }, [searchParams, selectedEvent]);

  // Load recent entries from sessionStorage
  useEffect(() => {
    if (selectedEvent) {
      const stored = sessionStorage.getItem(`usher_recent_${selectedEvent}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setRecentEntries(parsed.map((e: any) => ({ ...e, time: new Date(e.time) })));
        } catch (e) {
          console.error('Failed to parse recent entries', e);
        }
      } else {
        setRecentEntries([]);
      }
    }
  }, [selectedEvent]);

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => {
      const usherToken = searchParams.get('usher_token') || undefined;
      return attendeeAPI.selfRegister(selectedEvent, data, usherToken);
    },
    onSuccess: (response) => {
      const uniqueFields = getActiveUniqueFields(registrationData);
      const nameField = findFieldByName(uniqueFields, 'full_name');
      const name = (nameField ? formData[nameField.id] : '') || 'Guest';

      // Add to recent entries (cap at 10)
      const entry: RecentEntry = {
        id: String(Date.now()),
        name,
        time: new Date(),
        fields: { ...formData },
      };
      setRecentEntries(prev => {
        const updated = [entry, ...prev].slice(0, 10);
        if (selectedEvent) {
          sessionStorage.setItem(`usher_recent_${selectedEvent}`, JSON.stringify(updated));
        }
        return updated;
      });

      // Flash success and clear form
      setFlashSuccess(true);
      setFormData({});
      setErrors({});
      setTimeout(() => {
        setFlashSuccess(false);
        // Return focus to first field for next entry
        firstFieldRef.current?.focus();
      }, 1200);
    },
    onError: (error: any) => {
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || {};
        const errorMessages = Object.values(validationErrors).flat() as string[];
        
        if (errorMessages.length > 0) {
          const hasDuplicateError = errorMessages.some(msg => 
            msg.toLowerCase().includes('already been taken') || 
            msg.toLowerCase().includes('unique')
          );
          
          if (hasDuplicateError) {
            toast.error('Someone with this email or phone or both is already registered or has used this email or phone to register for this event. Please check and try again.');
          } else {
            toast.error(errorMessages[0]);
          }
        } else {
          toast.error('Please check the form and try again.');
        }
      } else {
        toast.error(error.message || 'Registration failed');
      }
    },
  });

  const handleChange = useCallback((fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: '' }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const fields = getActiveUniqueFields(registrationData);
    fields.forEach(field => {
      if (!field.is_active) return;
      const isRequired = field.is_required || field.is_mandatory_by_event;
      const hasValue = formData[field.id] !== undefined && formData[field.id] !== '';
      if (isRequired && !hasValue) {
        newErrors[field.id] = `${field.field_label} is required`;
      }
      if (hasValue && field.field_type === 'email') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.id])) {
          newErrors[field.id] = 'Invalid email address';
        }
      }
    });
    return newErrors;
  }, [registrationData, formData]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) return;

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const uniqueFields = getActiveUniqueFields(registrationData);
    const validation = validateAttendeeDataFormat(formData, uniqueFields);
    if (!validation.isValid) {
      setErrors({ general: validation.errors.join(', ') });
      return;
    }

    // Usher mode: no email consent popup, always false (admin action, not self-registration)
    registerMutation.mutate({ ...validation.data, email_consent: false });
  }, [selectedEvent, validate, formData, registrationData, registerMutation]);

  // Enter key on last field submits
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      const fields = getActiveUniqueFields(registrationData);
      const lastField = fields[fields.length - 1];
      if (lastField && formData[lastField.id] !== undefined) {
        handleSubmit(e as unknown as React.FormEvent);
      }
    }
  };

  const renderField = (field: any, index: number) => {
    const commonProps = {
      label: field.field_label,
      value: formData[field.id] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(field.id, e.target.value),
      error: errors[field.id],
      required: field.is_required,
      placeholder: field.placeholder || `Enter ${field.field_label.toLowerCase()}`,
    };

    const inputRef = index === 0 ? firstFieldRef : undefined;

    switch (field.field_type) {
      case 'email':
        return <Input key={field.id} {...commonProps} type="email" ref={inputRef} />;
      case 'phone':
        return <Input key={field.id} {...commonProps} type="tel" ref={inputRef} />;
      case 'number':
        return <Input key={field.id} {...commonProps} type="number" ref={inputRef} />;
      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.field_label}{field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={commonProps.placeholder}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
            {errors[field.id] && <p className="text-red-500 text-xs mt-1">{errors[field.id]}</p>}
          </div>
        );
      case 'dropdown':
      case 'select': {
        let options: string[] = [];
        const optionsData = field.field_options || field.options;
        if (optionsData) {
          options = typeof optionsData === 'string' ? JSON.parse(optionsData) : optionsData;
        }
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.field_label}{field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">Select {field.field_label}</option>
              {options.map((opt: string, i: number) => <option key={i} value={opt}>{opt}</option>)}
            </select>
            {errors[field.id] && <p className="text-red-500 text-xs mt-1">{errors[field.id]}</p>}
          </div>
        );
      }
      case 'radio': {
        let radioOptions: string[] = [];
        const rOptData = field.field_options || field.options;
        if (rOptData) {
          radioOptions = typeof rOptData === 'string' ? JSON.parse(rOptData) : rOptData;
        }
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.field_label}{field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-2">
              {radioOptions.map((opt: string, i: number) => (
                <label key={i} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name={field.id}
                    value={opt}
                    checked={formData[field.id] === opt}
                    onChange={() => handleChange(field.id, opt)}
                    className="text-primary-600"
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              ))}
            </div>
            {errors[field.id] && <p className="text-red-500 text-xs mt-1">{errors[field.id]}</p>}
          </div>
        );
      }
      case 'country':
        return (
          <CountrySelect
            key={field.id}
            {...commonProps}
            onChange={(val: string) => handleChange(field.id, val)}
          />
        );
      default:
        return <Input key={field.id} {...commonProps} type="text" ref={inputRef} />;
    }
  };

  const currentEvent = registrationData?.event || null;
  const activeFields = getActiveUniqueFields(registrationData);
  const primaryColor = currentEvent?.primary_color || '#0d9488';
  const registrationStatus = registrationData?.registration_status;
  const eventIdFromUrl = searchParams.get('event');
  const tokenFromUrl = searchParams.get('token');

  // Loading
  if (fieldsLoading && (eventIdFromUrl || tokenFromUrl)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
          <p className="text-gray-300">Loading usher view...</p>
        </div>
      </div>
    );
  }

  // Status screen (registration not open)
  if (registrationStatus && registrationStatus !== 'open' && !fieldsLoading) {
    const ev = registrationData?.event;
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md">
          <div className="p-8 text-white text-center" style={{ backgroundColor: ev?.primary_color || '#0d9488' }}>
            <h1 className="text-2xl font-bold">{ev?.name || 'Event'}</h1>
            <p className="text-sm mt-1 opacity-80">Usher Mode</p>
          </div>
          <div className="p-8 text-center">
            <AlertTriangle className="h-14 w-14 text-orange-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">
              {registrationStatus === 'not_yet_open' ? 'Registration Not Yet Open'
                : registrationStatus === 'closed' ? 'Registration Has Ended'
                : 'Registration Deactivated'}
            </h2>
            <p className="text-sm text-gray-500">Contact the event organizers to enable registration.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Top Bar */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <img
            src={currentEvent?.logo_url || defaultLogo}
            alt="logo"
            className="h-8 w-auto object-contain rounded"
          />
          <div>
            <h1 className="text-white font-bold text-sm leading-none">{currentEvent?.name || 'Event'}</h1>
            <p className="text-gray-400 text-xs flex items-center gap-1">
              <Zap className="h-3 w-3" style={{ color: primaryColor }} />
              Usher Fast-Entry Mode
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentEvent?.location && (
            <span className="text-gray-400 text-xs flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {currentEvent.location}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full"
            style={{ backgroundColor: primaryColor + '22', color: primaryColor }}>
            <Users className="h-3 w-3" />
            {recentEntries.length} registered today
          </span>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* === LEFT: Registration Form === */}
        <div className="lg:w-1/2 xl:w-2/5 p-4 lg:p-6 lg:overflow-y-auto">
          <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-700">
            {/* Form header */}
            <div className="px-5 py-4 border-b border-gray-700 flex items-center gap-2">
              <UserPlus className="h-5 w-5" style={{ color: primaryColor }} />
              <h2 className="text-white font-semibold text-sm">New Registration</h2>
              <span className="ml-auto text-xs text-gray-400">Press Enter to submit</span>
            </div>

            {/* Flash success */}
            {flashSuccess && (
              <div className="mx-5 mt-4 bg-green-900/50 border border-green-500/50 rounded-lg p-3 flex items-center gap-2 animate-pulse">
                <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                <span className="text-green-300 text-sm font-medium">Registered successfully!</span>
              </div>
            )}

            {/* Error banner */}
            {errors.general && (
              <div className="mx-5 mt-4 bg-red-900/50 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <span className="text-red-300 text-sm">{errors.general}</span>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="p-5 space-y-4">
              {activeFields.length === 0 && !fieldsLoading && (
                <p className="text-gray-400 text-sm text-center py-4">No form fields configured.</p>
              )}
              {activeFields.map((field, idx) => (
                <div key={field.id} className="[&_label]:text-gray-300 [&_label]:text-xs [&_input]:bg-gray-700 [&_input]:border-gray-600 [&_input]:text-white [&_input]:placeholder-gray-500 [&_select]:bg-gray-700 [&_select]:border-gray-600 [&_select]:text-white [&_textarea]:bg-gray-700 [&_textarea]:border-gray-600 [&_textarea]:text-white [&_textarea]:placeholder-gray-500">
                  {renderField(field, idx)}
                </div>
              ))}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={registerMutation.isPending}
                  className="w-full py-3 px-4 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 transition-all duration-150 active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: primaryColor }}
                >
                  {registerMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      Register Attendee
                      <ChevronRight className="h-4 w-4 ml-auto" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* === RIGHT: Recent Entries Panel === */}
        <div className="lg:w-1/2 xl:w-3/5 border-t lg:border-t-0 lg:border-l border-gray-700 flex flex-col min-h-0">
          <div className="px-5 py-4 border-b border-gray-700 flex items-center gap-2 bg-gray-800/50">
            <Clock className="h-4 w-4 text-gray-400" />
            <h2 className="text-gray-300 font-medium text-sm">Recent Entries</h2>
            <span className="ml-auto text-xs text-gray-500">This session</span>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {recentEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-800 border-2 border-dashed border-gray-600 flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-gray-600" />
                </div>
                <p className="text-gray-500 text-sm font-medium">No registrations yet</p>
                <p className="text-gray-600 text-xs mt-1">Entries will appear here after each registration</p>
              </div>
            ) : (
              recentEntries.map((entry, idx) => {
                const secondaryFields = activeFields.slice(1, 3);
                return (
                  <div
                    key={entry.id}
                    className={`flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-3 border transition-all duration-300 ${
                      idx === 0 ? 'border-green-500/40 shadow-green-500/10 shadow-md' : 'border-gray-700'
                    }`}
                  >
                    {/* Avatar */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ backgroundColor: primaryColor + 'cc' }}
                    >
                      {entry.name.charAt(0).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{entry.name}</p>
                      <p className="text-gray-400 text-xs truncate">
                        {secondaryFields.map(f => entry.fields[f.id]).filter(Boolean).join(' · ')}
                      </p>
                    </div>

                    {/* Time + badge */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {idx === 0 && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                          Just now
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">
                        {entry.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Summary footer */}
          {recentEntries.length > 0 && (
            <div className="border-t border-gray-700 px-5 py-3 bg-gray-800/50 flex items-center justify-between">
              <span className="text-gray-400 text-xs">Session total</span>
              <span className="text-white font-bold text-sm" style={{ color: primaryColor }}>
                {recentEntries.length} {recentEntries.length === 1 ? 'attendee' : 'attendees'}
              </span>
            </div>
          )}
        </div>
      </div>

      <Footer variant="registration" />
    </div>
  );
};

export default UsherRegistration;
